import {
  isValidOrganisationType,
  type CreateableOrganisationType,
} from "../isValidOrganisationType.js";
import { ORGANISATION_TYPE_URL_SEARCH_PARAM } from "../../constants.js";

/**
 * The role the visitor signs up as, carried in the `ot` URL parameter: `CLIENT`
 * is a property owner; `CRAFTSMAN`, any other value or none is a trade business,
 * the primary audience.
 */
export function getSignupRole(search: string): CreateableOrganisationType {
  return new URLSearchParams(search).get(ORGANISATION_TYPE_URL_SEARCH_PARAM) ===
    "CLIENT"
    ? "CLIENT"
    : "CRAFTSMAN";
}

export function getOtherSignupRole(
  role: CreateableOrganisationType
): CreateableOrganisationType {
  return role === "CLIENT" ? "CRAFTSMAN" : "CLIENT";
}

/** The query string with `ot` set to the role and every other parameter kept. */
export function withSignupRole(
  search: string,
  role: CreateableOrganisationType
): string {
  const params = new URLSearchParams(search);
  params.set(ORGANISATION_TYPE_URL_SEARCH_PARAM, role);
  return `?${params.toString()}`;
}

/**
 * The authorization request parameter that carries the role to Keycloak; the
 * identity-first provider reads it (as the client note
 * `client_request_param_signup_as`) before the redirect URI's `ot`, which the
 * native app's fixed callback URI cannot carry.
 */
export const SIGNUP_ROLE_REQUEST_PARAM = "signup_as";

/**
 * The Keycloak login URL with the role from `ot` as `signup_as`, unchanged
 * without a valid `ot`. Appended as is, so the URL keycloak-js built keeps its
 * own encoding.
 */
export function withSignupRoleRequestParam(
  loginUrl: string,
  search: string
): string {
  const role = new URLSearchParams(search).get(
    ORGANISATION_TYPE_URL_SEARCH_PARAM
  );
  if (role === null || !isValidOrganisationType(role)) {
    return loginUrl;
  }
  const separator = loginUrl.includes("?") ? "&" : "?";
  return `${loginUrl}${separator}${SIGNUP_ROLE_REQUEST_PARAM}=${encodeURIComponent(role)}`;
}

/**
 * The authorization request parameter that carries the company of a CRM
 * linking invitation to Keycloak, the same name the registration prefill uses;
 * the identity-first provider forwards it (as the client note
 * `client_request_param_msys_invitation_company`) into its registration
 * redirect.
 */
export const INVITATION_COMPANY_REQUEST_PARAM = "msys_invitation_company";

/** The Keycloak login URL with the invitation's company as parameter, unchanged without one. */
export function withInvitationCompanyRequestParam(
  loginUrl: string,
  company: string | undefined
): string {
  if (!company) {
    return loginUrl;
  }
  const separator = loginUrl.includes("?") ? "&" : "?";
  return `${loginUrl}${separator}${INVITATION_COMPANY_REQUEST_PARAM}=${encodeURIComponent(company)}`;
}
